// components/Loaders/TableSkeleton.tsx
import React from "react";

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  withHeader?: boolean;
  withActions?: boolean;
  compact?: boolean;
}

const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 6,
  columns = 5,
  withHeader = true,
  withActions = false,
  compact = false
}) => {
  const totalColumns = withActions ? columns + 1 : columns;
  const rowHeight = compact ? "h-3" : "h-4";
  const paddingY = compact ? "py-2" : "py-3";

  return (
    <div className="w-full overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm bg-white dark:bg-gray-900">
      {/* Table Header */}
      {withHeader && (
        <div className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 animate-pulse"></div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded w-32 animate-pulse"></div>
              <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded w-24 animate-pulse"></div>
            </div>
          </div>
        </div>
      )}

      {/* Table Content */}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              {Array.from({ length: totalColumns }).map((_, index) => (
                <th
                  key={index}
                  className={`px-4 ${paddingY} text-left text-sm font-semibold text-gray-600 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700`}
                >
                  <div className={`${rowHeight} bg-gray-200 dark:bg-gray-700 rounded animate-pulse ${
                    index === 0 ? "w-32" : index === totalColumns - 1 && withActions ? "w-20" : "w-24"
                  }`}></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {Array.from({ length: rows }).map((_, rowIdx) => (
              <tr 
                key={rowIdx} 
                className="group hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors"
              >
                {Array.from({ length: totalColumns }).map((_, colIdx) => (
                  <td key={colIdx} className={`px-4 ${paddingY} align-middle`}>
                    <div className="flex items-center space-x-2">
                      {/* First column often has different content */}
                      {colIdx === 0 && (
                        <div className="flex-shrink-0">
                          <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
                        </div>
                      )}
                      
                      <div className={`${rowHeight} bg-gray-200 dark:bg-gray-700 rounded animate-pulse flex-1 ${
                        // Vary widths for more natural look
                        colIdx === 0 ? "w-32" :
                        colIdx === 1 ? "w-48" :
                        colIdx === totalColumns - 1 && withActions ? "w-20" :
                        rowIdx % 3 === 0 ? "w-36" :
                        rowIdx % 3 === 1 ? "w-28" : "w-40"
                      }`}></div>
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Table Footer with Pagination Skeleton */}
      <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 animate-pulse"></div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-8 animate-pulse"></div>
            <div className="flex items-center space-x-1">
              {[1, 2, 3].map((page) => (
                <div
                  key={page}
                  className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-8 animate-pulse"
                ></div>
              ))}
            </div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-8 animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Compact version for tighter spaces
export const CompactTableSkeleton: React.FC<Omit<TableSkeletonProps, 'compact'>> = (props) => (
  <TableSkeleton {...props} compact />
);

// Card-based table skeleton for modern layouts
export const CardTableSkeleton: React.FC<TableSkeletonProps> = ({ rows = 5, columns = 4 }) => {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      {/* Card Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse"></div>
          <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded w-32 animate-pulse"></div>
        </div>
      </div>

      {/* Table Content */}
      <div className="p-6">
        <div className="space-y-4">
          {Array.from({ length: rows }).map((_, rowIdx) => (
            <div
              key={rowIdx}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/30 rounded-lg border border-gray-100 dark:border-gray-700"
            >
              <div className="flex items-center space-x-4 flex-1">
                {Array.from({ length: columns }).map((_, colIdx) => (
                  <div
                    key={colIdx}
                    className="flex-1"
                  >
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2 w-3/4"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-1/2"></div>
                  </div>
                ))}
              </div>
              <div className="flex items-center space-x-2 ml-4">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-8 animate-pulse"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-8 animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Card Footer */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800/30 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 animate-pulse"></div>
          <div className="flex items-center space-x-2">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-20 animate-pulse"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-20 animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Loading state with shimmer effect
export const ShimmerTableSkeleton: React.FC<TableSkeletonProps> = (props) => {
  return (
    <div className="relative overflow-hidden">
      <TableSkeleton {...props} />
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/30 dark:via-gray-800/30 to-transparent"></div>
    </div>
  );
};

export default TableSkeleton;